import { Star } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { useRequestGameContext } from "#/lib/useRequestGameContext";
import { cn } from "#/lib/utils";
import { getMyMapRating } from "#/server/getMyMapRating";
import { submitMapRating } from "#/server/submitMapRating";

const STAR_VALUES = [1, 2, 3, 4, 5] as const;

type RatingStatus = "loading" | "ready" | "thanks";

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <Star
      className={cn(
        "size-8 transition-colors",
        filled ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/50",
      )}
    />
  );
}

function StarRow({ filledCount }: { filledCount: number }) {
  return (
    <div className="flex w-fit items-center justify-center gap-1 mx-auto">
      {STAR_VALUES.map((value) => (
        <span key={value} className="p-0.5">
          <StarIcon filled={value <= filledCount} />
        </span>
      ))}
    </div>
  );
}

function RatingStars({ onRate }: { onRate: (rating: number) => void }) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  return (
    <div
      className="flex w-fit items-center justify-center gap-1 mx-auto"
      onMouseLeave={() => setHoverValue(null)}
    >
      {STAR_VALUES.map((value) => (
        <button
          key={value}
          type="button"
          aria-label={`Rate ${value} ${value === 1 ? "star" : "stars"}`}
          className="rounded p-0.5 transition-transform hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          onMouseEnter={() => setHoverValue(value)}
          onFocus={() => setHoverValue(value)}
          onClick={() => onRate(value)}
        >
          <StarIcon filled={value <= (hoverValue ?? 0)} />
        </button>
      ))}
    </div>
  );
}

function RatingPanel({ children, title }: { children: ReactNode; title: string }) {
  return (
    <div className="flex w-full flex-col gap-3 rounded-lg bg-background p-3 ring-1 ring-foreground/10">
      <p className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      {children}
    </div>
  );
}

function useMapRatingStatus(mapId: string) {
  const [status, setStatus] = useState<RatingStatus>("loading");
  const [savedRating, setSavedRating] = useState(0);

  useEffect(() => {
    let active = true;
    getMyMapRating({ data: { mapId } })
      .then(({ rating }) => {
        if (!active) return;
        if (rating === null) {
          setStatus("ready");
          return;
        }
        setSavedRating(rating);
        setStatus("thanks");
      })
      .catch(() => {
        if (active) setStatus("ready");
      });
    return () => {
      active = false;
    };
  }, [mapId]);

  return { savedRating, setSavedRating, setStatus, status };
}

function MapRatingForPlayer({ mapId }: { mapId: string }) {
  const { savedRating, setSavedRating, setStatus, status } = useMapRatingStatus(mapId);

  if (status === "loading") {
    return (
      <RatingPanel title="Rate map">
        <div aria-busy="true" aria-label="Loading rating" className="pointer-events-none">
          <StarRow filledCount={0} />
        </div>
      </RatingPanel>
    );
  }

  if (status === "thanks") {
    return (
      <RatingPanel title="Thanks for rating">
        <StarRow filledCount={savedRating} />
      </RatingPanel>
    );
  }

  const handleRate = (rating: number) => {
    setSavedRating(rating);
    setStatus("thanks");
    void submitMapRating({ data: { mapId, rating } }).catch(() => setStatus("ready"));
  };

  return (
    <RatingPanel title="Rate map">
      <RatingStars onRate={handleRate} />
    </RatingPanel>
  );
}

export function MapRating({ mapId }: { mapId: string }) {
  const { isAnonymous } = useRequestGameContext();

  if (isAnonymous) {
    return null;
  }

  return <MapRatingForPlayer mapId={mapId} />;
}
